import { useI18n } from "@baton/i18n";
import { Dialog } from "@baton/ui";
import { lazy, Suspense, useState, useSyncExternalStore } from "react";
import { useRuntime } from "../../app/RuntimeProvider.tsx";
import { DialogFormSkeleton } from "../../skeletons/Skeletons.tsx";

/** The re-authentication dialog's chunk, which carries the password sign-in form. */
export const loadReauthDialog = () => import("./ReauthDialog.tsx");

const ReauthDialog = lazy(() => loadReauthDialog().then((module) => ({ default: module.ReauthDialog })));

/**
 * FR-015-013 and FR-015-120: the re-authentication dialog and its sign-in form stay out of the
 * initial load. The gate mounts the dialog the first time `runtime.reauth` requires it and keeps
 * it mounted afterwards, so later 401s open it without a wait. The entry prefetches the chunk on
 * idle (`main.tsx`), because a deploy can remove the old chunk from the server before the
 * session expires; until the chunk arrives the dialog's frame shows its form skeleton.
 */
export function ReauthGate() {
  const { t } = useI18n();
  const runtime = useRuntime();
  const required = useSyncExternalStore(runtime.reauth.subscribe, () => runtime.reauth.required);
  const [mounted, setMounted] = useState(false);
  if (required && !mounted) setMounted(true);
  if (!mounted) return null;
  return (
    <Suspense
      fallback={
        <Dialog
          open={required}
          dismissible={false}
          size="sm"
          title={t("reauth.title")}
          description={t("reauth.body")}
          onOpenChange={(open) => {
            if (!open) runtime.reauth.dismissed();
          }}
        >
          <DialogFormSkeleton fields={2} />
        </Dialog>
      }
    >
      <ReauthDialog />
    </Suspense>
  );
}
